import { Box, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import React from "react";

const INDIGO = "#4F46E5";
const INDIGO_SOFT = "#EEF0FF";
const BORDER = "#E4E7EC";
const BORDER_HOVER = "#C7CAD1";
const DOT_BORDER = "#D0D5DD";

const RadioIndicator = ({ selected }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: "50%",
      flexShrink: 0,
      border: `2px solid ${selected ? INDIGO : DOT_BORDER}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {selected && (
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: INDIGO }} />
    )}
  </Box>
);

// Square counterpart to RadioIndicator, so a multi-select group reads as
// checkboxes rather than radios.
const CheckboxIndicator = ({ selected }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: "4px",
      flexShrink: 0,
      border: `2px solid ${selected ? INDIGO : DOT_BORDER}`,
      background: selected ? INDIGO : "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {selected && <Check sx={{ fontSize: "0.75rem", color: "#fff" }} />}
  </Box>
);

// A single selectable option. `indicator` picks the trailing affordance:
// "radio" always shows the dot, "checkbox" always shows the box, and "check"
// shows a bare tick only once selected.
export const OptionChip = ({
  selected,
  disabled = false,
  onClick,
  label,
  subtitle,
  icon,
  indicator = "radio",
  sx = {},
}) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-pressed={!!selected}
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => {
      if (!disabled && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick?.(e);
      }
    }}
    sx={{
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      borderRadius: "10px",
      p: "8px 14px",
      width: "fit-content",
      display: "inline-flex",
      alignItems: "center",
      gap: icon ? 0.75 : 1,
      border: `1.5px solid ${selected ? INDIGO : BORDER}`,
      background: selected ? INDIGO_SOFT : "#fff",
      transition: "all .15s ease",
      "&:hover": { borderColor: selected || disabled ? undefined : BORDER_HOVER },
      ...sx,
    }}
  >
        {indicator === "check" && selected && (
      <Check sx={{ color: INDIGO, fontSize: "1.1rem", flexShrink: 0 }} />
    )}
    {indicator === "checkbox" && <CheckboxIndicator selected={selected} />}
    {indicator === "radio" && <RadioIndicator selected={selected} />}
    <Box>
      <Typography
        sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#101828", whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "0.72rem", color: "#667085", whiteSpace: "nowrap" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// Option shapes differ across the API (`value`/`name`, `_id`/`name`) and the
// local stage list (`key`/`title`), so both accessors fall back across them.
const defaultGetValue = (option) => option?.value ?? option?._id ?? option?.key;
const defaultGetLabel = (option) => option?.name ?? option?.label ?? option?.title;

// Ids arrive as strings from the API but a formik value may be a number, so
// compare stringified — an unset value ("", null, undefined) matches nothing.
const isSame = (a, b) =>
  a !== undefined && a !== null && a !== "" && String(a) === String(b);

// A wrapping row of chips. Chips size to their content, so the row wraps rather
// than each chip stretching across a grid column.
//
// Single-select (default): `value` is one option's value, and `onChange` gets
// the clicked option's value.
// Multi-select (`multiple`): `value` is an array, and `onChange` gets the next
// array with the clicked option toggled in or out.
// Either way `onChange` receives `(nextValue, option, index)`.
export const OptionChipGroup = ({
  options,
  value,
  onChange,
  multiple = false,
  getOptionValue = defaultGetValue,
  getOptionLabel = defaultGetLabel,
  getOptionSubtitle,
  getOptionIcon,
  getOptionDisabled,
  indicator = multiple ? "checkbox" : "radio",
  children,
  sx = {},
}) => {
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : [];

  const isSelected = (optionValue) =>
    multiple
      ? selectedValues.some((selected) => isSame(selected, optionValue))
      : isSame(value, optionValue);

  // Toggling preserves the order the user clicked in, and re-selecting an
  // already-chosen chip removes it.
  const nextValue = (optionValue) => {
    if (!multiple) return optionValue;
    return isSelected(optionValue)
      ? selectedValues.filter((selected) => !isSame(selected, optionValue))
      : [...selectedValues, optionValue];
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, ...sx }}>
      {Array.isArray(options)
        ? options.map((option, index) => {
            const optionValue = getOptionValue(option, index);
            return (
              <OptionChip
                key={optionValue ?? index}
                selected={isSelected(optionValue)}
                disabled={getOptionDisabled?.(option, index)}
                onClick={() => onChange?.(nextValue(optionValue), option, index)}
                label={getOptionLabel(option, index)}
                subtitle={getOptionSubtitle?.(option, index)}
                icon={getOptionIcon?.(option, index)}
                indicator={indicator}
              />
            );
          })
        : children}
    </Box>
  );
};

export default OptionChipGroup;

